import React, { FC, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { Dashboard } from 'api/dashboard';
import { Button } from 'components/atoms/button';
import { Form } from 'components/atoms/form';
import { Input } from 'components/atoms/input';
import { Modal } from 'components/atoms/modal';
import { Radio } from 'components/atoms/radio';
import { useUpdateDashboardMutation } from 'queries/dashboard';
import { useDashboardToastMessage } from '../common';
import { DashboardForm, SharedType } from '../dashboard-constants';
import { Footer, FormItem, Options } from './component';

export interface EditDashboardProps {
  isShown: boolean;
  onClose: () => void;
  initialValue: DashboardForm;
  isDefault?: boolean;
}

export const EditDashboardModal: FC<EditDashboardProps> = ({
  isShown,
  onClose,
  initialValue,
  isDefault = false,
}) => {
  const [dashboardForm] = Form.useForm<DashboardForm>();
  const { mutate } = useUpdateDashboardMutation();
  const { showEditMessage } = useDashboardToastMessage();

  const onSubmit = (value: DashboardForm) => {
    if (!initialValue.dashboardId) {
      return;
    }
    const payload = {
      ...value,
      dashboardId: initialValue.dashboardId,
    };

    mutate(payload, {
      onSuccess: (dashboard: Dashboard) => {
        onClose();
        showEditMessage(dashboard.dashboardName);
      },
      onError: () => {
        showEditMessage(payload.dashboardName, false);
      },
    });
  };

  const onCancel = () => {
    dashboardForm.resetFields();
    onClose();
  };

  const ModalFooter = () => {
    return (
      <Footer onCancel={onCancel}>
        <Button type="primary" htmlType="submit" onClick={dashboardForm.submit}>
          <FormattedMessage id="save" />
        </Button>
      </Footer>
    );
  };

  // dashboard form isn't initializing if given invoked again and again with different values. https://stackoverflow.com/a/63644146/5277189
  useEffect(() => {
    if (!isShown) {
      return;
    }
    dashboardForm.setFieldsValue(initialValue);
  }, [dashboardForm, initialValue, isShown]);

  useEffect(() => {
    if (!isShown) {
      return;
    }
    // Ref inside antd modal isn't attaching properly.
    // Modal itself refreshers after some time and the whole dom repaints.
    // below is working for now
    queueMicrotask(() => {
      const nameEl = document.getElementById('editDashboardName');

      if (!nameEl) {
        return;
      }
      nameEl.focus();
    });
  }, [isShown]);

  return (
    <Modal
      visible={isShown}
      title={<FormattedMessage id="dashboard.edit_dashboard" />}
      width={462}
      onCancel={onCancel}
      footer={<ModalFooter />}
    >
      <Form
        layout="vertical"
        form={dashboardForm}
        name="dashboardForm"
        initialValues={initialValue}
        onFinish={(value) => onSubmit(value as DashboardForm)}
      >
        <FormItem
          key="dashboardName"
          name="dashboardName"
          label={<FormattedMessage id="name" />}
          rules={[
            {
              required: true,
              message: <FormattedMessage id="input.required" />,
              type: 'string',
            },
          ]}
        >
          <Input
            placeholder="e.g: Weekly dashboard"
            autoComplete="off"
            autoFocus
            id="editDashboardName"
          />
        </FormItem>

        {!isDefault && (
          <FormItem
            key="shared"
            name="shared"
            label={<FormattedMessage id="dashboard.model.shared" />}
          >
            <Radio.Group style={{ width: '100%' }}>
              <Options>
                <Radio value={SharedType.ME} key="me">
                  <FormattedMessage id="views.view_available.options.me" />
                </Radio>
                <Radio value={SharedType.TEAM} key="team">
                  <FormattedMessage id="views.view_available.options.team" />
                </Radio>
              </Options>
            </Radio.Group>
          </FormItem>
        )}
      </Form>
    </Modal>
  );
};
